import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  BackLink,
  FieldLabel,
  FooterPrompt,
  Headline,
  PageEyebrow,
  SplitShell,
} from '../components/auth-shells.js';
import { magToast } from '../components/toaster.js';
import { requestPasswordReset, signIn } from '../lib/auth-client.js';

const searchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute('/forgot-password')({
  validateSearch: searchSchema,
  component: ForgotPasswordPage,
});

// Handoff §15.2.5 — split shell, napping Mag — a bit embarrassed for you.
// Top "← back to log in" mono link, inline magic-link shortcut in the
// subtitle, "↘ don't have an account? sign up" footer prompt.
function ForgotPasswordPage() {
  const { t } = useLingui();

  const { email: initialEmail } = Route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [pending, setPending] = useState<'reset' | 'magic' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending('reset');
    const result = await requestPasswordReset({
      email,
      // Email passes through so the new-password screen can address the user
      // by name. Better Auth appends `?token=…` (or `&token=…` if there's
      // already a query string) to whatever we hand it.
      redirectTo: `${window.location.origin}/reset-password?email=${encodeURIComponent(email)}`,
    });
    if (result.error) {
      magToast.error(
        i18n._(msg`couldn't send the reset link.`),
        result.error.message ?? i18n._(msg`try again in a sec.`),
      );
      setPending(null);
      return;
    }
    navigate({ to: '/forgot-password/sent', search: { email } });
  };

  const handleMagic = async () => {
    if (!email) {
      magToast.warning(
        i18n._(msg`enter your email first.`),
        i18n._(msg`i'll send the link there.`),
      );
      return;
    }
    setPending('magic');
    const result = await signIn.magicLink({
      email,
      callbackURL: `${window.location.origin}/home`,
    });
    if (result.error) {
      magToast.error(
        i18n._(msg`couldn't send the magic link.`),
        result.error.message ?? i18n._(msg`try again in a sec.`),
      );
      setPending(null);
      return;
    }
    navigate({ to: '/magic-link/sent', search: { email } });
  };

  return (
    <SplitShell quote={t`happens to all of us. tell me your email and i'll fix it.`}>
      <BackLink to="/login">
        <Trans>back to log in</Trans>
      </BackLink>

      <div className="mt-6">
        <PageEyebrow>
          <Trans>forgot password</Trans>
        </PageEyebrow>
        <div className="mt-3 flex flex-col gap-3">
          <Headline>
            <Trans>let's reset it.</Trans>
          </Headline>
          <p className="text-[15px] leading-[1.5] text-[var(--ink)]/80">
            <Trans>
              type your email and we'll send a reset link. Or{' '}
              <button type="button" onClick={handleMagic} className="link-mag font-semibold">
                skip the password and use a magic link →
              </button>
            </Trans>
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
        <label className="field">
          <FieldLabel>
            <Trans>email on the account</Trans>
          </FieldLabel>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t`founder@yourstartup.com`}
          />
        </label>

        <button
          type="submit"
          disabled={pending !== null}
          className="btn mt-2 w-full disabled:opacity-60"
        >
          {pending === 'reset' ? (
            <Trans>sending…</Trans>
          ) : pending === 'magic' ? (
            <Trans>sending magic link…</Trans>
          ) : (
            <Trans>send reset link</Trans>
          )}
          <span className="btn-arrow">→</span>
        </button>

        <FooterPrompt to="/signup" prompt={t`don't have an account?`} cta={t`sign up`} />
      </form>
    </SplitShell>
  );
}
